// Pure visibility gate shared by the docs manifest filter (useVisibleDocs)
// and the help-drawer filter (useVisibleHelpTopics).
//
// This is the SINGLE source of truth for "can this user see this piece of
// MDX content?". Extracting it as a pure function (no hooks) means docs and
// help can never drift on the gating rules — both hooks assemble a `DocsGate`
// from useMenuVisibility + useAuth and call this.
//
// Rules (must stay byte-for-byte identical to the original useVisibleDocs):
//   - Module/submenu gate: NO superadmin bypass — a superadmin sees a
//     module's content only when that module is visible for the account
//     (same rule as the module nav). Universal content (no module) skips it.
//   - Feature-flag gate: content with `featureFlag` needs that account flag
//     on, exactly like a flagged submenu in the nav (SectionSubNav). No
//     superadmin bypass: the nav has none either.
//   - Role gate: only applies when the content specifies an allow-list.
//     Bypassed for SUPPORT and superadmins (internal staff). FAIL-CLOSED:
//     a missing role on role-gated content is denied, never bypassed.

import type { AccountRole } from "@verity/shared-types";

export interface DocsGate {
  isMenuVisible: (menuKey: string) => boolean;
  isSubmenuVisible: (menuKey: string, submenuKey: string) => boolean;
  role: AccountRole | undefined;
  isSuperadmin: boolean;
  /** The active account's feature flags (`activeAccount.featureFlags`). */
  featureFlags?: Record<string, boolean | undefined> | null;
}

/** Frontmatter fields the gate reads — satisfied by both DocMeta and HelpTopicMeta. */
export interface GatedMeta {
  module?: string;
  submenu?: string;
  roles?: AccountRole[];
  featureFlag?: string;
}

export function isDocVisible(meta: GatedMeta, gate: DocsGate): boolean {
  // Module / submenu gate — universal content (no `module`) skips both.
  // No superadmin bypass here (same rule as the module nav).
  if (meta.module && !gate.isMenuVisible(meta.module)) return false;
  if (meta.module && meta.submenu && !gate.isSubmenuVisible(meta.module, meta.submenu)) {
    return false;
  }

  // Feature-flag gate — the same check the nav makes for a flagged submenu.
  if (meta.featureFlag && gate.featureFlags?.[meta.featureFlag] !== true) return false;

  // Role gate — only when an allow-list is present. Bypassed for SUPPORT
  // and superadmins. No role at all → fail closed (a missing role must
  // never bypass a role-gated item).
  if (meta.roles?.length) {
    if (gate.isSuperadmin || gate.role === "SUPPORT") {
      // bypass — internal staff
    } else if (!gate.role || !meta.roles.includes(gate.role)) {
      return false;
    }
  }

  return true;
}
